import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Chip, 
  CircularProgress, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import apiRequest from '../api/axios';

interface DashboardMetrics {
  new_signups_today: number;
  first_purchase_today: number;
  total_purchases_today: number;
  total_redeems_today: number;
  total_ggr: number;
  yesterday_ggr: number;
  week_ggr: number;
  week_purchases: number;
  last_week_ggr: number;
  last_week_purchases: number;
  month_purchases: number;
  month_redeems: number;
  month_ggr: number;
}

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  created_at: string;
  phone_number?: string;
  affiliate_id?: string;
}

interface Transaction {
  id: number;
  user_id: number;
  amount: number;
  status: string;
  initiated_at: string;
  payment_method: string;
  user: {
    email: string;
    first_name: string;
    last_name: string;
  };
}

interface DashboardMetricsProps {
  tenantId?: number;
  variant?: 'header' | 'sidebar';
}

const DashboardMetrics: React.FC<DashboardMetricsProps> = ({ tenantId, variant = 'header' }) => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [demoMode, setDemoMode] = useState(false);
  
  // Modal states
  const [newPlayersModalOpen, setNewPlayersModalOpen] = useState(false);
  const [firstPurchasesModalOpen, setFirstPurchasesModalOpen] = useState(false);
  const [newPlayers, setNewPlayers] = useState<User[]>([]);
  const [firstPurchases, setFirstPurchases] = useState<Transaction[]>([]);
  const [loadingNewPlayers, setLoadingNewPlayers] = useState(false);
  const [loadingFirstPurchases, setLoadingFirstPurchases] = useState(false);

  // Demo mode multipliers
  const DEMO_MULTIPLIERS = {
    deposits: 5.0,    // 500% (5x)
    redeems: 2.0,     // 200% (2x)
  };

  // Load demo mode from localStorage on component mount
  useEffect(() => {
    const savedDemoMode = localStorage.getItem('admin_demo_mode') === 'true';
    setDemoMode(savedDemoMode);
  }, []);



  // Apply demo multipliers to metrics
  const getDemoMetrics = (originalMetrics: DashboardMetrics): DashboardMetrics => {
    if (!demoMode) return originalMetrics;

    const demoDeposits = originalMetrics.total_purchases_today * DEMO_MULTIPLIERS.deposits;
    const demoRedeems = originalMetrics.total_redeems_today * DEMO_MULTIPLIERS.redeems;
    const demoGGR = demoDeposits - demoRedeems;

    const demoYesterdayDeposits = (originalMetrics.yesterday_ggr + originalMetrics.total_redeems_today) * DEMO_MULTIPLIERS.deposits;
    const demoYesterdayRedeems = originalMetrics.total_redeems_today * DEMO_MULTIPLIERS.redeems;
    const demoYesterdayGGR = demoYesterdayDeposits - demoYesterdayRedeems;

    const demoWeekDeposits = originalMetrics.week_purchases * DEMO_MULTIPLIERS.deposits;
    const demoWeekRedeems = originalMetrics.total_redeems_today * DEMO_MULTIPLIERS.redeems;
    const demoWeekGGR = demoWeekDeposits - demoWeekRedeems;

    const demoLastWeekPurchases = originalMetrics.last_week_purchases * DEMO_MULTIPLIERS.deposits;
    const demoLastWeekGGR = originalMetrics.last_week_ggr * DEMO_MULTIPLIERS.deposits;

    const demoMonthDeposits = originalMetrics.month_purchases * DEMO_MULTIPLIERS.deposits;
    const demoMonthRedeems = originalMetrics.month_redeems * DEMO_MULTIPLIERS.redeems;
    const demoMonthGGR = demoMonthDeposits - demoMonthRedeems;

    return {
      ...originalMetrics,
      total_purchases_today: demoDeposits,
      total_redeems_today: demoRedeems,
      total_ggr: demoGGR,
      yesterday_ggr: demoYesterdayGGR,
      week_ggr: demoWeekGGR,
      week_purchases: demoWeekDeposits,
      last_week_ggr: demoLastWeekGGR,
      last_week_purchases: demoLastWeekPurchases,
      month_purchases: demoMonthDeposits,
      month_redeems: demoMonthRedeems,
      month_ggr: demoMonthGGR,
    };
  };

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      console.log('DashboardMetrics: Token from localStorage:', token ? 'EXISTS' : 'MISSING');
      
      const params = tenantId ? { tenant_id: tenantId } : {};
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      console.log('DashboardMetrics: Request headers:', headers);
      
      const response = await apiRequest.get('/api/v1/admin/dashboard-metrics', { 
        params,
        headers
      });
      setMetrics(response.data as DashboardMetrics);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch dashboard metrics:', err);
      setError('Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const fetchNewPlayers = async () => {
    try {
      setLoadingNewPlayers(true);
      const token = localStorage.getItem('token');
      const params = tenantId ? { tenant_id: tenantId } : {};
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await apiRequest.get('/api/v1/admin/new-players-today', { 
        params,
        headers
      });
      setNewPlayers(response.data as User[]);
    } catch (err) {
      console.error('Failed to fetch new players:', err);
    } finally {
      setLoadingNewPlayers(false);
    }
  };

  const fetchFirstPurchases = async () => {
    try {
      setLoadingFirstPurchases(true);
      const token = localStorage.getItem('token');
      const params = tenantId ? { tenant_id: tenantId } : {};
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await apiRequest.get('/api/v1/admin/first-purchases-today', { 
        params,
        headers
      });
      setFirstPurchases(response.data as Transaction[]);
    } catch (err) {
      console.error('Failed to fetch first purchases:', err);
    } finally {
      setLoadingFirstPurchases(false);
    }
  };

  const handleNewPlayersClick = () => {
    setNewPlayersModalOpen(true);
    fetchNewPlayers();
  };

  const handleFirstPurchasesClick = () => {
    setFirstPurchasesModalOpen(true);
    fetchFirstPurchases();
  };

  useEffect(() => {
    // Add a small delay to ensure token is available
    const timer = setTimeout(() => {
      fetchMetrics();
    }, 100);
    
    // Refresh metrics every 5 minutes
    const interval = setInterval(fetchMetrics, 5 * 60 * 1000);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [tenantId, fetchMetrics]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={16} />
        <Typography variant="caption">Loading...</Typography>
      </Box>
    );
  }

  if (error || !metrics) {
    return (
      <Typography variant="caption" color="error">
        {error || 'No data available'}
      </Typography>
    );
  }

  // Apply demo multipliers if demo mode is enabled
  const displayMetrics = getDemoMetrics(metrics);

  if (variant === 'header') {
    return (
      <>
        <Box display="flex" alignItems="center" gap={2} sx={{ ml: 2 }}>
          <Chip
            label={`New Sign Ups Today: ${displayMetrics.new_signups_today}`}
            size="small"
            onClick={handleNewPlayersClick}
            sx={{
              backgroundColor: 'white',
              color: 'primary.main',
              border: '1px solid white',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                transform: 'scale(1.02)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          />
          <Chip
            label={`First Purchase Today: ${displayMetrics.first_purchase_today}`}
            size="small"
            onClick={handleFirstPurchasesClick}
            sx={{
              backgroundColor: 'white',
              color: 'secondary.main',
              border: '1px solid white',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                transform: 'scale(1.02)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          />

        </Box>

        {/* New Players Modal */}
        <Dialog 
          open={newPlayersModalOpen} 
          onClose={() => setNewPlayersModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">New Players Today</Typography>
              <IconButton onClick={() => setNewPlayersModalOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {loadingNewPlayers ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Affiliate ID</TableCell>
                      <TableCell>Sign Up Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {newPlayers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          {user.first_name} {user.last_name}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.phone_number || 'N/A'}</TableCell>
                        <TableCell>{user.affiliate_id || 'N/A'}</TableCell>
                        <TableCell>{formatDate(user.created_at)}</TableCell>
                      </TableRow>
                    ))}
                    {newPlayers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No new players today
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNewPlayersModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* First Purchases Modal */}
        <Dialog 
          open={firstPurchasesModalOpen} 
          onClose={() => setFirstPurchasesModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">First Purchases Today</Typography>
              <IconButton onClick={() => setFirstPurchasesModalOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {loadingFirstPurchases ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Payment Method</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Purchase Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {firstPurchases.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          {transaction.user.first_name} {transaction.user.last_name}
                          <br />
                          <Typography variant="caption" color="textSecondary">
                            {transaction.user.email}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatCurrency(transaction.amount)}</TableCell>
                        <TableCell>{transaction.payment_method}</TableCell>
                        <TableCell>
                          <Chip 
                            label={transaction.status} 
                            size="small"
                            color={transaction.status === 'SUCCESS' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{formatDate(transaction.initiated_at)}</TableCell>
                      </TableRow>
                    ))}
                    {firstPurchases.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No first purchases today
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFirstPurchasesModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Sidebar variant
  return (
    <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
             <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
         <Typography variant="h6">
           Today's Metrics
         </Typography>
       </Box>
       
      <Box display="flex" flexDirection="column" gap={0.5}>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2">Total Purchases:</Typography>
          <Typography variant="body2" fontWeight="bold" color="success.main">
            {formatCurrency(displayMetrics.total_purchases_today)}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2">Total Redeems:</Typography>
          <Typography variant="body2" fontWeight="bold" color="error.main">
            {formatCurrency(displayMetrics.total_redeems_today)}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2" fontWeight="bold">Total GGR:</Typography>
          <Typography 
            variant="body2" 
            fontWeight="bold" 
            color={displayMetrics.total_ggr >= 0 ? 'success.main' : 'error.main'}
          >
            {formatCurrency(displayMetrics.total_ggr)}
          </Typography>
        </Box>
      </Box>
      
      <Typography variant="h6" gutterBottom sx={{ mt: 1.5 }}>
        Historical GGR
      </Typography>
      <Box display="flex" flexDirection="column" gap={0.5}>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2">Yesterday's GGR:</Typography>
          <Typography 
            variant="body2" 
            fontWeight="bold" 
            color={displayMetrics.yesterday_ggr >= 0 ? 'success.main' : 'error.main'}
          >
            {formatCurrency(displayMetrics.yesterday_ggr)}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2">Week to Date GGR:</Typography>
          <Typography 
            variant="body2" 
            fontWeight="bold" 
            color={displayMetrics.week_ggr >= 0 ? 'success.main' : 'error.main'}
          >
            {formatCurrency(displayMetrics.week_ggr)}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2">Week to Date Purchases:</Typography>
          <Typography 
            variant="body2" 
            fontWeight="bold" 
            color="success.main"
          >
            {formatCurrency(displayMetrics.week_purchases)}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2">Last Week Total Purchases:</Typography>
          <Typography 
            variant="body2" 
            fontWeight="bold" 
            color="success.main"
          >
            {formatCurrency(displayMetrics.last_week_purchases)}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2">Last Week GGR:</Typography>
          <Typography 
            variant="body2" 
            fontWeight="bold" 
            color={displayMetrics.last_week_ggr >= 0 ? 'success.main' : 'error.main'}
          >
            {formatCurrency(displayMetrics.last_week_ggr)}
          </Typography>
        </Box>
      </Box>
      
      <Typography variant="h6" gutterBottom sx={{ mt: 1.5 }}>
        Month to Date
      </Typography>
      <Box display="flex" flexDirection="column" gap={0.5}>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2">MTD Purchases:</Typography>
          <Typography variant="body2" fontWeight="bold" color="success.main">
            {formatCurrency(displayMetrics.month_purchases)}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2">MTD Redeems:</Typography>
          <Typography variant="body2" fontWeight="bold" color="error.main">
            {formatCurrency(displayMetrics.month_redeems)}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2" fontWeight="bold">MTD GGR:</Typography>
          <Typography 
            variant="body2" 
            fontWeight="bold" 
            color={displayMetrics.month_ggr >= 0 ? 'success.main' : 'error.main'}
          >
            {formatCurrency(displayMetrics.month_ggr)}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2">Net Holding %:</Typography>
          <Typography 
            variant="body2" 
            fontWeight="bold" 
            color={displayMetrics.month_purchases > 0 ? 'primary.main' : 'text.secondary'}
          >
            {displayMetrics.month_purchases > 0 
              ? `${((displayMetrics.month_ggr / displayMetrics.month_purchases) * 100).toFixed(2)}%`
              : '0.00%'
            }
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardMetrics;
